import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  Braces,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  ExternalLink,
  FileStack,
  FlaskConical,
  GitFork,
  KeyRound,
  Maximize2,
  Minimize2,
  Play,
  RotateCcw,
  Save,
  Search,
  SlidersHorizontal,
  Trash2,
  X
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
  type SyntheticEvent
} from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { apiDownload, apiRequest } from "../../lib/api";
import {
  agentProfileSchema,
  agentProfileRecordSchema,
  applicationListSchema,
  approvalDecisionSchema,
  capabilityListSchema,
  connectionTestSchema,
  createdKeySchema,
  fabricResourceListSchema,
  fabricResourceSchema,
  profileListSchema,
  secretListSchema,
  storedSecretSchema,
  taskCanceledSchema,
  taskCreatedSchema,
  taskDetailEnvelopeSchema,
  virtualModelSchema,
  type AgentProfile,
  type AgentProfileRecord,
  type Capability,
  type FabricResource,
  type ModelRoute,
  type TaskDetail as TaskDetailData,
  type TaskGraph,
  type TaskSummary,
  type VirtualKey,
  type VirtualModel
} from "../../lib/schemas";
import { nodeRegistry } from "./nodeRegistry";
import {
  capabilityEstimatedCost,
  capabilityLatencyMs,
  capabilityUpstreamModel,
  channelModelCapabilities,
  channelModelSummary
} from "./channelModels";
import { deriveCreationDefaults, type CreationDefaults } from "./derivation";
import {
  advancedConfiguration,
  applyLlmPreset,
  buildA2aConfiguration,
  buildLlmConfiguration,
  buildMcpConfiguration,
  configurationRecord,
  detectLlmPreset,
  llmConnection,
  llmModels,
  mcpTransport,
  parseAdvancedConfiguration,
  type LlmModel,
  type LlmConnectionPreset,
  type LlmProviderKind,
  type McpTransport
} from "./endpointConfiguration";
import { formatProfileValidationError, parseOptionalJson } from "./agentProfileForm";
import { normalizeTaskGraph, TaskGraphEditor, validateTaskGraph } from "./TaskGraphEditor";
import type { CanvasSelection, ControlCanvasNode, DraftKind } from "./types";

interface InspectorProps {
  selection: CanvasSelection;
  onClose: () => void;
  onCreate: (kind: DraftKind, source?: ControlCanvasNode) => void;
}

const inspectorMinimumWidth = 460;
const inspectorMaximumWidth = 900;
const inspectorCanvasReserve = 280;

function clampInspectorWidth(width: number) {
  return Math.min(
    Math.max(width, inspectorMinimumWidth),
    Math.min(inspectorMaximumWidth, window.innerWidth - inspectorCanvasReserve)
  );
}

export function Inspector({ selection, onClose, onCreate }: InspectorProps) {
  const [expanded, setExpanded] = useState(false);
  const [manualWidth, setManualWidth] = useState<number | null>(null);
  const inspectorRef = useRef<HTMLElement>(null);
  if (!selection) return null;
  const kind = selection.mode === "create" ? selection.kind : selection.node.data.kind;
  const title =
    selection.mode === "create" ? `New ${nodeRegistry[kind].label}` : selection.node.data.title;
  const resizeFromPointer = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const right = inspectorRef.current?.getBoundingClientRect().right ?? window.innerWidth;
    setManualWidth(clampInspectorWidth(right - event.clientX));
  };
  const resizeFromKeyboard = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Home") {
      event.preventDefault();
      setExpanded(false);
      setManualWidth(null);
      return;
    }
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const currentWidth =
      inspectorRef.current?.getBoundingClientRect().width ?? inspectorMinimumWidth;
    const direction = event.key === "ArrowLeft" ? 1 : -1;
    setExpanded(false);
    setManualWidth(clampInspectorWidth(currentWidth + direction * (event.shiftKey ? 64 : 32)));
  };
  return (
    <aside
      ref={inspectorRef}
      className={`canvas-inspector${expanded ? " is-expanded" : ""}`}
      aria-label="Node inspector"
      style={
        manualWidth === null
          ? undefined
          : ({ "--manual-inspector-width": `${String(manualWidth)}px` } as CSSProperties)
      }
    >
      <button
        className="inspector-resize-handle"
        type="button"
        aria-label="Resize inspector"
        title="Drag to resize inspector. Use arrow keys for precise adjustment."
        onDoubleClick={() => {
          setExpanded(false);
          setManualWidth(null);
        }}
        onKeyDown={resizeFromKeyboard}
        onPointerDown={(event) => {
          setExpanded(false);
          event.currentTarget.setPointerCapture(event.pointerId);
          resizeFromPointer(event);
        }}
        onPointerMove={resizeFromPointer}
        onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
      />
      <header className="inspector-header">
        <div>
          <span>{selection.mode === "create" ? "Create node" : nodeRegistry[kind].label}</span>
          <strong>{title}</strong>
        </div>
        <div className="inspector-header-actions">
          <button
            className="icon-button canvas-inspector-expand"
            type="button"
            title={expanded ? "Restore inspector width" : "Expand inspector"}
            aria-label={expanded ? "Restore inspector width" : "Expand inspector"}
            onClick={() => {
              setManualWidth(null);
              setExpanded((value) => !value);
            }}
          >
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button className="icon-button" type="button" title="Close inspector" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </header>
      <div className="inspector-scroll">
        {selection.mode === "create" && selection.source && (
          <div className="derivation-context">
            <GitFork size={14} />
            <span>
              Derived from <strong>{selection.source.data.title}</strong>
            </span>
          </div>
        )}
        {selection.mode === "create" ? (
          <CreateInspector
            kind={selection.kind}
            {...(selection.source ? { source: selection.source } : {})}
            onDone={onClose}
          />
        ) : (
          <ResourceInspector selection={selection} onDone={onClose} />
        )}
        {selection.mode === "resource" && selection.node.data.outputs.length > 0 && (
          <section className="derive-section">
            <span className="field-label">Derive connected resource</span>
            <div className="derive-actions">
              {nodeRegistry[kind].derives.map((next) => (
                <button
                  className="quiet-button compact"
                  type="button"
                  key={next}
                  onClick={() => onCreate(next, selection.node)}
                >
                  + {nodeRegistry[next].label}
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </aside>
  );
}

function CreateInspector({
  kind,
  source,
  onDone
}: {
  kind: DraftKind;
  source?: ControlCanvasNode;
  onDone: () => void;
}) {
  const defaults = deriveCreationDefaults(kind, source?.data.resource);
  if (kind === "llm" || kind === "mcp" || kind === "a2a") {
    return <ResourceForm key={kind} kind={kind} onDone={onDone} />;
  }
  if (kind === "model")
    return <VirtualModelForm defaults={defaults.virtualModelRoutes} onDone={onDone} />;
  if (kind === "agent") return <AgentForm defaults={defaults} onDone={onDone} />;
  if (kind === "key") return <KeyForm defaults={defaults} onDone={onDone} />;
  return <TaskForm defaults={defaults} onDone={onDone} />;
}

function ResourceInspector({
  selection,
  onDone
}: {
  selection: Exclude<CanvasSelection, null | { mode: "create" }>;
  onDone: () => void;
}) {
  const resource = selection.node.data.resource;
  if (resource.type === "resource") {
    const kind =
      resource.value.kind === "channel"
        ? "llm"
        : resource.value.kind === "mcp_server"
          ? "mcp"
          : "a2a";
    return (
      <ResourceForm key={resource.value.id} kind={kind} resource={resource.value} onDone={onDone} />
    );
  }
  if (resource.type === "agent") return <AgentDetail value={resource.value} onDone={onDone} />;
  if (resource.type === "virtual_model")
    return <VirtualModelForm model={resource.value} onDone={onDone} />;
  if (resource.type === "key") return <KeyDetail value={resource.value} onDone={onDone} />;
  if (resource.type === "task") return <TaskRuntimeInspector value={resource.value} />;
  return <CapabilityDetail value={resource.value} onDone={onDone} />;
}

const endpointTemplates: Record<"llm" | "mcp" | "a2a", unknown> = {
  llm: {
    name: "provider",
    kind: "ollama",
    base_url: "http://127.0.0.1:11434",
    weight: 100,
    timeout_seconds: 30,
    models: []
  },
  mcp: {
    name: "local-tools",
    trust_domain: "local",
    transport: "stdio",
    program: "",
    args: [],
    env: {}
  },
  a2a: { name: "remote-agent", url: "http://127.0.0.1:9000" }
};

function resourceKindFor(kind: "llm" | "mcp" | "a2a"): "channel" | "mcp_server" | "a2a_endpoint" {
  return kind === "llm" ? "channel" : kind === "mcp" ? "mcp_server" : "a2a_endpoint";
}

function ResourceForm({
  kind,
  resource,
  onDone
}: {
  kind: "llm" | "mcp" | "a2a";
  resource?: FabricResource;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [message, setMessage] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState(false);
  const [secretRef, setSecretRef] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [newCredentialName] = useState(() => `channel-${crypto.randomUUID()}`);
  const formRef = useRef<HTMLFormElement>(null);
  const initialConfiguration = resource?.configuration ?? endpointTemplates[kind];
  const [models, setModels] = useState<LlmModel[]>(() => llmModels(initialConfiguration));
  const [transport, setTransport] = useState<McpTransport>(() =>
    mcpTransport(initialConfiguration)
  );
  const secrets = useQuery({
    queryKey: ["secrets"],
    queryFn: () => apiRequest("/v1/admin/secrets", secretListSchema)
  });
  useEffect(() => {
    if (!resource?.secret_id || !secrets.data) return;
    const name = secrets.data.data.find((secret) => secret.id === resource.secret_id)?.name;
    if (name) setSecretRef(name);
  }, [resource?.secret_id, secrets.data]);

  const create = useMutation({
    mutationFn: async (payload: {
      name: string;
      configuration: Record<string, unknown>;
      credential?: { name: string; value: string };
    }) => {
      let secretName: string | undefined;
      if (payload.credential) {
        await apiRequest(
          `/v1/admin/secrets/${encodeURIComponent(payload.credential.name)}`,
          storedSecretSchema,
          { method: "PUT", body: JSON.stringify({ value: payload.credential.value }) }
        );
        secretName = payload.credential.name;
      }
      try {
        return await apiRequest("/v1/admin/fabric/resources", fabricResourceSchema, {
          method: "POST",
          body: JSON.stringify({
            kind: resourceKindFor(kind),
            name: payload.name,
            configuration: payload.configuration,
            secret_name: secretName
          })
        });
      } catch (error) {
        if (secretName) {
          await apiRequest(`/v1/admin/secrets/${encodeURIComponent(secretName)}`, z.null(), {
            method: "DELETE"
          }).catch(() => undefined);
        }
        throw error;
      }
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["resources"] });
      onDone();
    }
  });
  const publish = useMutation({
    mutationFn: (payload: {
      name: string;
      configuration: Record<string, unknown>;
      credential?: { name: string; value: string };
    }) => {
      const storeCredential = payload.credential
        ? apiRequest(
            `/v1/admin/secrets/${encodeURIComponent(payload.credential.name)}`,
            storedSecretSchema,
            { method: "PUT", body: JSON.stringify({ value: payload.credential.value }) }
          )
        : Promise.resolve(null);
      return storeCredential.then(async () => {
        const secretName = payload.credential
          ? payload.credential.name
          : payload.configuration.secret_name;
        return apiRequest(
          `/v1/admin/fabric/resources/${encodeURIComponent(resource?.id ?? "")}/publish`,
          fabricResourceSchema,
          {
            method: "POST",
            body: JSON.stringify({
              configuration: payload.configuration,
              secret_name: typeof secretName === "string" ? secretName : undefined,
              expected_revision: resource?.active_revision ?? 1
            })
          }
        );
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["resources"] });
      onDone();
    }
  });
  const remove = useMutation({
    mutationFn: () =>
      apiRequest(`/v1/admin/fabric/resources/${resource?.id ?? ""}`, z.null(), {
        method: "DELETE"
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["resources"] });
      onDone();
    }
  });
  const rollback = useMutation({
    mutationFn: () =>
      apiRequest(
        `/v1/admin/fabric/resources/${resource?.id ?? ""}/rollback`,
        fabricResourceSchema,
        {
          method: "POST"
        }
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["resources"] });
      onDone();
    }
  });
  const test = useMutation({
    mutationFn: () =>
      apiRequest(`/v1/admin/fabric/resources/${resource?.id ?? ""}/test`, connectionTestSchema, {
        method: "POST"
      }),
    onSuccess: (result) =>
      setMessage(
        result.ok
          ? `Connection passed in ${String(result.latency_ms)}ms${result.discovered_models > 0 ? ` / ${String(result.discovered_models)} models` : ""}`
          : `Connection failed with status ${String(result.status)}`
      )
  });
  const refresh = useMutation({
    mutationFn: () =>
      apiRequest(`/v1/admin/fabric/resources/${resource?.id ?? ""}/refresh`, capabilityListSchema, {
        method: "POST"
      }),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: ["resources"] });
      setMessage(
        kind === "llm"
          ? `Model catalog refreshed (${String(result.data.length)} capabilities)`
          : "Capability discovery refreshed"
      );
    }
  });
  const connectAndFetch = useMutation({
    mutationFn: async () => {
      const form = formRef.current;
      if (!form) return;
      const data = new FormData(form);
      const configuration = buildLlmConfiguration(
        parseAdvancedConfiguration(data.get("configuration")),
        {
          name: text(data.get("name")).trim() || "onboarding-preview",
          kind: text(data.get("provider_kind")),
          baseUrl: text(data.get("base_url")).trim(),
          weight: Number(data.get("weight")),
          timeoutSeconds: Number(data.get("timeout_seconds")),
          models: []
        }
      );
      setMessage("");
      let secretName: string | undefined;
      if (apiKey.trim()) {
        await apiRequest(
          `/v1/admin/secrets/${encodeURIComponent(newCredentialName)}`,
          storedSecretSchema,
          { method: "PUT", body: JSON.stringify({ value: apiKey }) }
        );
        secretName = newCredentialName;
      }
      try {
        const created = await apiRequest("/v1/admin/fabric/resources", fabricResourceSchema, {
          method: "POST",
          body: JSON.stringify({
            kind: "channel",
            name: text(data.get("name")).trim() || "onboarding-preview",
            configuration,
            secret_name: secretName
          })
        });
        await apiRequest(`/v1/admin/fabric/resources/${created.id}/refresh`, capabilityListSchema, {
          method: "POST"
        });
        await client.invalidateQueries({ queryKey: ["resources"] });
        onDone();
      } catch (error) {
        if (secretName) {
          await apiRequest(`/v1/admin/secrets/${encodeURIComponent(secretName)}`, z.null(), {
            method: "DELETE"
          }).catch(() => undefined);
        }
        throw error;
      }
    }
  });
  function buildConfiguration(data: FormData) {
    const advanced = parseAdvancedConfiguration(data.get("configuration"));
    const name = text(data.get("name")).trim();
    if (kind === "llm") {
      return {
        configuration: buildLlmConfiguration(advanced, {
          name,
          kind: text(data.get("provider_kind")),
          baseUrl: text(data.get("base_url")).trim(),
          weight: Number(data.get("weight")),
          timeoutSeconds: Number(data.get("timeout_seconds")),
          models
        }),
        name
      };
    }
    if (kind === "mcp") {
      const record = configurationRecord(advanced);
      const configuration =
        transport === "stdio"
          ? buildMcpConfiguration(advanced, {
              name,
              transport: "stdio",
              trustDomain: text(data.get("trust_domain")).trim() || "local",
              program: text(data.get("program")).trim(),
              args: text(data.get("args"))
                .split("\n")
                .map((item) => item.trim())
                .filter(Boolean),
              env: JSON.parse(text(data.get("env")) || "{}") as object
            })
          : buildMcpConfiguration(advanced, {
              name,
              transport,
              trustDomain: text(data.get("trust_domain")).trim() || "local",
              url: text(data.get("url")).trim()
            });
      if (record.bearer_token === null && transport !== "stdio") {
        configuration.bearer_token = null;
      }
      return { configuration, name };
    }
    return {
      configuration: buildA2aConfiguration(advanced, name, text(data.get("url")).trim()),
      name
    };
  }
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      setMessage("");
      const { configuration, name } = buildConfiguration(form);
      const credential =
        apiKey.trim() || (!resource && secretRef && newCredentialName === secretRef)
          ? { name: secretRef || newCredentialName, value: apiKey }
          : undefined;
      if (resource) {
        publish.mutate(credential ? { name, configuration, credential } : { name, configuration });
      } else {
        create.mutate(credential ? { name, configuration, credential } : { name, configuration });
      }
    } catch (error) {
      setMessage(toError(error).message);
    }
  }
  const saveError = resource ? publish.error : create.error;
  const pending = resource ? publish.isPending : create.isPending || connectAndFetch.isPending;
  return (
    <InspectorForm
      onSubmit={submit}
      error={
        saveError ??
        remove.error ??
        rollback.error ??
        test.error ??
        refresh.error ??
        connectAndFetch.error
      }
      message={message}
      formRef={formRef}
    >
      <label>
        Name
        <input name="name" required defaultValue={resource?.name ?? ""} />
      </label>
      {kind === "llm" && (
        <LlmEndpointFields
          configuration={initialConfiguration}
          models={models}
          onModels={setModels}
          onConnectionChange={() => {
            if (!resource) setModels([]);
          }}
        />
      )}
      {kind === "mcp" && (
        <McpEndpointFields
          configuration={initialConfiguration}
          transport={transport}
          onTransport={setTransport}
        />
      )}
      {kind === "a2a" && <A2aEndpointFields configuration={initialConfiguration} />}
      {kind === "llm" && !resource && (
        <section className="credential-field" aria-label="Channel connection">
          <label>
            API key
            <input
              type="password"
              autoComplete="new-password"
              value={apiKey}
              placeholder="Optional for local or unauthenticated channels"
              onChange={(event) => {
                setApiKey(event.currentTarget.value);
                setModels([]);
              }}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            disabled={connectAndFetch.isPending}
            onClick={() => connectAndFetch.mutate()}
          >
            <Search size={15} />{" "}
            {connectAndFetch.isPending ? "Connecting…" : "Connect and fetch models"}
          </button>
          <small>
            The key is used for this connection check, encrypted as a secret, and referenced by the
            channel when you save.
          </small>
        </section>
      )}
      {(kind !== "mcp" || transport !== "stdio") && (
        <CredentialField value={secretRef} onChange={setSecretRef} />
      )}
      {kind === "llm" && resource && <ModelCatalog resource={resource} />}
      <AgentFormSection
        title="Advanced configuration"
        description="Adapter-specific JSON"
        icon={<Braces size={14} />}
      >
        <label>
          Raw configuration
          <textarea
            name="configuration"
            rows={10}
            className="mono"
            defaultValue={JSON.stringify(
              advancedConfiguration(kind, initialConfiguration),
              null,
              2
            )}
          />
        </label>
      </AgentFormSection>
      <div className="inspector-actions">
        <button
          className="primary-button"
          type="submit"
          disabled={pending || (kind === "llm" && !resource && models.length === 0)}
        >
          <Save size={15} /> {resource ? "Publish revision" : "Create resource"}
        </button>
        {resource && (
          <button
            className="quiet-button"
            type="button"
            disabled={test.isPending}
            onClick={() => test.mutate()}
          >
            <FlaskConical size={15} /> {endpointActionLabel(kind, "test")}
          </button>
        )}
        {resource && (
          <button
            className="quiet-button"
            type="button"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            <RotateCcw size={15} /> {endpointActionLabel(kind, "refresh")}
          </button>
        )}
        {resource && resource.active_revision > 1 && (
          <button
            className="quiet-button"
            type="button"
            disabled={rollback.isPending}
            onClick={() => setConfirmRollback(true)}
          >
            <RotateCcw size={15} /> Roll back revision
          </button>
        )}
        {resource && (
          <button
            className="icon-button danger"
            type="button"
            title="Delete resource"
            disabled={remove.isPending}
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title="Delete resource?"
          description={`This removes ${resource?.name ?? "this resource"} from the fabric. Existing grants and task snapshots are not rewritten.`}
          confirmLabel="Delete resource"
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            remove.mutate();
          }}
        />
      )}
      {confirmRollback && (
        <ConfirmDialog
          title="Roll back revision?"
          description={`${resource?.name ?? "This resource"} returns to its previous published configuration. Models routed through it are not rewritten.`}
          confirmLabel="Roll back"
          pending={rollback.isPending}
          onCancel={() => setConfirmRollback(false)}
          onConfirm={() => {
            setConfirmRollback(false);
            rollback.mutate();
          }}
        />
      )}
    </InspectorForm>
  );
}

function CredentialField({
  value,
  onChange
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const client = useQueryClient();
  const [credentialName, setCredentialName] = useState(value);
  const [credentialValue, setCredentialValue] = useState("");
  const [message, setMessage] = useState("");
  const secrets = useQuery({
    queryKey: ["secrets"],
    queryFn: () => apiRequest("/v1/admin/secrets", secretListSchema)
  });
  const store = useMutation({
    mutationFn: ({ name, secret }: { name: string; secret: string }) =>
      apiRequest(`/v1/admin/secrets/${encodeURIComponent(name)}`, storedSecretSchema, {
        method: "PUT",
        body: JSON.stringify({ value: secret })
      }),
    onSuccess: async (result) => {
      onChange(result.name);
      setCredentialName(result.name);
      setCredentialValue("");
      setMessage("Credential stored");
      await client.invalidateQueries({ queryKey: ["secrets"] });
    }
  });
  const knownNames = new Set(secrets.data?.data.map((secret) => secret.name) ?? []);
  if (value) knownNames.add(value);
  return (
    <section className="credential-field">
      <label>
        Credential
        <select
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setCredentialName(event.target.value);
          }}
        >
          <option value="">No credential</option>
          {[...knownNames].sort().map((name) => (
            <option value={name} key={name}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <AgentFormSection
        title="Credential vault"
        description={value ? "Rotate or replace selected secret" : "Create encrypted secret"}
        icon={<KeyRound size={14} />}
      >
        <label>
          Secret name
          <input
            value={credentialName}
            onChange={(event) => setCredentialName(event.target.value)}
            placeholder="provider-production"
          />
        </label>
        <label>
          Secret value
          <input
            type="password"
            autoComplete="new-password"
            value={credentialValue}
            onChange={(event) => setCredentialValue(event.target.value)}
            placeholder="Write only"
          />
        </label>
        <button
          className="quiet-button"
          type="button"
          disabled={!credentialName.trim() || !credentialValue || store.isPending}
          onClick={() => store.mutate({ name: credentialName.trim(), secret: credentialValue })}
        >
          <Save size={14} />{" "}
          {knownNames.has(credentialName) ? "Rotate credential" : "Store credential"}
        </button>
        {secrets.isLoading && <p className="inspector-inline-state">Loading credentials</p>}
        {secrets.error instanceof Error && (
          <p className="form-message error">{secrets.error.message}</p>
        )}
        {store.error instanceof Error && (
          <p className="form-message error">{store.error.message}</p>
        )}
        {message && <p className="form-message">{message}</p>}
      </AgentFormSection>
    </section>
  );
}

function ModelCatalog({ resource }: { resource: FabricResource }) {
  const client = useQueryClient();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [reviewing, setReviewing] = useState<Capability | null>(null);
  const [confirmExclude, setConfirmExclude] = useState<Capability | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [batchDecision, setBatchDecision] = useState<"approve" | "exclude" | null>(null);
  const [batchResult, setBatchResult] = useState<string | null>(null);
  const decision = useMutation({
    mutationFn: ({ capability, enabled }: { capability: Capability; enabled: boolean }) =>
      apiRequest(`/v1/admin/fabric/capabilities/${capability.id}/approve`, z.null(), {
        method: "POST",
        body: JSON.stringify({ enabled })
      }),
    onSuccess: async () => {
      setReviewing(null);
      await client.invalidateQueries({ queryKey: ["resources"] });
    }
  });
  const batch = useMutation({
    mutationFn: async ({ items, enabled }: { items: Capability[]; enabled: boolean }) => {
      const outcomes = await Promise.allSettled(
        items.map((capability) =>
          apiRequest(`/v1/admin/fabric/capabilities/${capability.id}/approve`, z.null(), {
            method: "POST",
            body: JSON.stringify({ enabled })
          })
        )
      );
      const failed = outcomes.flatMap((outcome, index) =>
        outcome.status === "rejected"
          ? [`${items[index]?.namespace ?? "Unknown model"}: ${toError(outcome.reason).message}`]
          : []
      );
      return { attempted: items, failed };
    },
    onSuccess: async ({ attempted, failed }) => {
      const failedKeys = new Set(failed.map((message) => message.split(":")[0] ?? message));
      setSelected((current) => {
        const next = new Set(current);
        attempted.forEach((capability) => {
          if (!failedKeys.has(capability.namespace)) next.delete(capability.id);
        });
        return next;
      });
      setBatchResult(
        failed.length === 0
          ? `${String(attempted.length)} capabilities updated`
          : `${String(attempted.length - failed.length)} updated; ${String(failed.length)} failed: ${failed.join(" | ")}`
      );
      await client.invalidateQueries({ queryKey: ["resources"] });
    }
  });
  const capabilities = channelModelCapabilities(resource);
  const normalizedQuery = query.trim().toLowerCase();
  const models = capabilities
    .map((capability) => ({
      capability,
      displayStatus: capability.enabled ? "approved" : "pending"
    }))
    .filter(
      ({ capability, displayStatus }) =>
        (status === "all" || displayStatus === status) &&
        (!normalizedQuery ||
          `${capability.namespace} ${capabilityUpstreamModel(capability)}`
            .toLowerCase()
            .includes(normalizedQuery))
    );
  const summary = channelModelSummary(resource);
  const selectableVisible = models.filter(({ capability }) => !capability.enabled);
  const allVisibleSelected =
    selectableVisible.length > 0 &&
    selectableVisible.every(({ capability }) => selected.has(capability.id));
  const pendingBatch = batchDecision
    ? capabilities.filter((capability) => selected.has(capability.id) && !capability.enabled)
    : [];
  return (
    <AgentFormSection
      title="Model catalog"
      description={`${String(summary.active)} active / ${String(summary.pending)} pending`}
      icon={<Search size={14} />}
    >
      <div className="model-catalog-toolbar">
        <input
          aria-label="Filter catalog models"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder="Filter discovered models"
        />
        <select
          aria-label="Filter catalog status"
          value={status}
          onChange={(event) => setStatus(event.currentTarget.value)}
        >
          <option value="all">All</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
        </select>
      </div>
      {selectableVisible.length > 0 && (
        <div className="model-catalog-selection">
          <label className="check-row">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={(event) =>
                setSelected(
                  event.currentTarget.checked
                    ? new Set(selectableVisible.map(({ capability }) => capability.id))
                    : new Set()
                )
              }
            />
            Select visible
          </label>
          <span>{selected.size} selected</span>
          <button
            className="quiet-button compact"
            type="button"
            disabled={selected.size === 0}
            onClick={() => setBatchDecision("approve")}
          >
            Approve selected
          </button>
          <button
            className="quiet-button compact danger"
            type="button"
            disabled={selected.size === 0}
            onClick={() => setBatchDecision("exclude")}
          >
            Disable selected
          </button>
        </div>
      )}
      {models.length === 0 && <p className="inspector-inline-state">No matching models</p>}
      <div className="model-catalog-list">
        {models.map(({ capability, displayStatus }) => (
          <div className="model-catalog-row" key={capability.id}>
            <input
              type="checkbox"
              checked={selected.has(capability.id)}
              disabled={capability.enabled}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setSelected((current) => {
                  const next = new Set(current);
                  if (checked) next.add(capability.id);
                  else next.delete(capability.id);
                  return next;
                });
              }}
            />
            <div>
              <strong>{capability.namespace}</strong>
              <span>{capabilityUpstreamModel(capability)}</span>
            </div>
            <span className={`catalog-status status-${displayStatus}`}>{displayStatus}</span>
            {capability.enabled ? (
              <button
                className="quiet-button compact danger"
                type="button"
                onClick={() => setConfirmExclude(capability)}
              >
                Disable
              </button>
            ) : (
              <button
                className="quiet-button compact"
                type="button"
                onClick={() => setReviewing(capability)}
              >
                Review
              </button>
            )}
          </div>
        ))}
      </div>
      {reviewing && (
        <CapabilityReview
          capability={reviewing}
          pending={decision.isPending}
          onClose={() => setReviewing(null)}
          onApprove={(enabled) => decision.mutate({ capability: reviewing, enabled })}
        />
      )}
      {decision.error instanceof Error && (
        <p className="form-message error">{decision.error.message}</p>
      )}
      {batch.error instanceof Error && <p className="form-message error">{batch.error.message}</p>}
      {batchResult && <p className="form-message">{batchResult}</p>}
      {batchDecision && pendingBatch.length > 0 && (
        <ConfirmDialog
          title={`${batchDecision === "approve" ? "Approve" : "Disable"} selected models?`}
          description={`${String(pendingBatch.length)} selected models will be ${batchDecision === "approve" ? "enabled for routing" : "disabled"}. Other catalog models are unchanged.`}
          confirmLabel={`${batchDecision === "approve" ? "Approve" : "Disable"} ${String(pendingBatch.length)} models`}
          pending={batch.isPending}
          onCancel={() => setBatchDecision(null)}
          onConfirm={() => {
            const decision = batchDecision;
            const items = pendingBatch;
            setBatchDecision(null);
            batch.mutate({ items, enabled: decision === "approve" });
          }}
        />
      )}
      {confirmExclude && (
        <ConfirmDialog
          title="Disable model?"
          description={`${confirmExclude.namespace} will be removed from routing until it is approved again.`}
          confirmLabel="Disable model"
          pending={decision.isPending}
          onCancel={() => setConfirmExclude(null)}
          onConfirm={() => {
            const capability = confirmExclude;
            setConfirmExclude(null);
            decision.mutate({ capability, enabled: false });
          }}
        />
      )}
    </AgentFormSection>
  );
}

function CapabilityReview({
  capability,
  pending,
  onClose,
  onApprove
}: {
  capability: Capability;
  pending: boolean;
  onClose: () => void;
  onApprove: (enabled: boolean) => void;
}) {
  return (
    <section className="discovered-model-review" aria-label="Review discovered model">
      <header>
        <div>
          <span>Capability route</span>
          <strong>{capability.namespace}</strong>
        </div>
        <button className="icon-button" type="button" title="Close model review" onClick={onClose}>
          <X size={14} />
        </button>
      </header>
      <DetailList
        rows={[
          ["Upstream model", capabilityUpstreamModel(capability)],
          ["Channel", capability.resource_name],
          ["Kind", capability.kind],
          ["Estimated cost", formatMoney(capabilityEstimatedCost(capability))],
          ["Latency", `${String(capabilityLatencyMs(capability))}ms`]
        ]}
      />
      <div className="inspector-actions">
        <button
          className="primary-button"
          type="button"
          disabled={pending}
          onClick={() => onApprove(true)}
        >
          <Check size={15} /> Approve model
        </button>
        <button
          className="quiet-button danger"
          type="button"
          disabled={pending}
          onClick={() => onApprove(false)}
        >
          <Ban size={15} /> Disable
        </button>
      </div>
    </section>
  );
}

const modelCapabilities = [
  ["text", "Text"],
  ["tools", "Tools"],
  ["vision", "Vision"],
  ["embeddings", "Embeddings"],
  ["image_generation", "Image generation"],
  ["image_editing", "Image editing"]
] as const;

function LlmEndpointFields({
  configuration,
  models,
  onModels,
  onConnectionChange
}: {
  configuration: unknown;
  models: LlmModel[];
  onModels: (models: LlmModel[]) => void;
  onConnectionChange: () => void;
}) {
  const record = configurationRecord(configuration);
  const [connection, setConnection] = useState(() => llmConnection(configuration));
  const [preset, setPreset] = useState(() => detectLlmPreset(llmConnection(configuration)));
  function updateModel(index: number, update: Partial<LlmModel>) {
    onModels(models.map((model, current) => (current === index ? { ...model, ...update } : model)));
  }
  return (
    <>
      <label>
        Connection preset
        <select
          value={preset}
          onChange={(event) => {
            const next = event.target.value as LlmConnectionPreset;
            onConnectionChange();
            setPreset(next);
            setConnection(applyLlmPreset(next, connection));
          }}
        >
          <option value="openai_official">OpenAI official</option>
          <option value="anthropic_official">Anthropic official</option>
          <option value="xai_official">xAI (Grok) official</option>
          <option value="ollama_local">Ollama local</option>
          <option value="openai_compatible">OpenAI-compatible relay</option>
          <option value="custom">Custom adapter</option>
        </select>
      </label>
      <label>
        Provider adapter
        <select
          name="provider_kind"
          value={connection.kind}
          onChange={(event) => {
            onConnectionChange();
            setPreset("custom");
            setConnection((current) => ({
              ...current,
              kind: event.target.value as LlmProviderKind
            }));
          }}
        >
          <option value="open_ai_compatible">OpenAI compatible</option>
          <option value="anthropic">Anthropic</option>
          <option value="xai">xAI (Grok)</option>
          <option value="ollama">Ollama</option>
        </select>
      </label>
      <label>
        Base URL
        <input
          name="base_url"
          type="url"
          required
          value={connection.baseUrl}
          onChange={(event) => {
            onConnectionChange();
            if (preset !== "openai_compatible" && preset !== "custom") setPreset("custom");
            setConnection((current) => ({ ...current, baseUrl: event.target.value }));
          }}
        />
      </label>
      <div className="field-pair">
        <label>
          Routing weight
          <input
            name="weight"
            type="number"
            min="1"
            defaultValue={numberValue(record.weight, 100)}
          />
        </label>
        <label>
          Timeout (seconds)
          <input
            name="timeout_seconds"
            type="number"
            min="1"
            defaultValue={numberValue(record.timeout_seconds, 30)}
          />
        </label>
      </div>
      <section className="endpoint-collection" aria-label="Model aliases">
        <div className="endpoint-section-heading">
          <span className="field-label">Model aliases</span>
          <button
            className="quiet-button compact"
            type="button"
            onClick={() =>
              onModels([
                ...models,
                {
                  public_name: "",
                  upstream_name: "",
                  capabilities: ["text"],
                  input_cost_per_million: 0,
                  output_cost_per_million: 0
                }
              ])
            }
          >
            + Model
          </button>
        </div>
        {models.length === 0 && <p className="inspector-inline-state">No static aliases</p>}
        {models.map((model, index) => (
          <fieldset className="endpoint-model" key={index}>
            <legend>Model {String(index + 1)}</legend>
            <div className="field-pair">
              <label>
                Public alias
                <input
                  required
                  value={model.public_name}
                  onChange={(event) => updateModel(index, { public_name: event.target.value })}
                />
              </label>
              <label>
                Upstream model
                <input
                  required
                  value={model.upstream_name}
                  onChange={(event) => updateModel(index, { upstream_name: event.target.value })}
                />
              </label>
            </div>
            <div className="capability-grid" aria-label={`Model ${String(index + 1)} capabilities`}>
              {modelCapabilities.map(([value, label]) => (
                <label className="check-row" key={value}>
                  <input
                    type="checkbox"
                    checked={model.capabilities.includes(value)}
                    onChange={(event) =>
                      updateModel(index, {
                        capabilities: event.target.checked
                          ? [...model.capabilities, value]
                          : model.capabilities.filter((item) => item !== value)
                      })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="field-pair">
              <label>
                Input / 1M
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={model.input_cost_per_million}
                  onChange={(event) =>
                    updateModel(index, { input_cost_per_million: Number(event.target.value) })
                  }
                />
              </label>
              <label>
                Output / 1M
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={model.output_cost_per_million}
                  onChange={(event) =>
                    updateModel(index, { output_cost_per_million: Number(event.target.value) })
                  }
                />
              </label>
            </div>
            <button
              className="quiet-button danger compact"
              type="button"
              onClick={() => onModels(models.filter((_, current) => current !== index))}
            >
              <Trash2 size={13} /> Remove model
            </button>
          </fieldset>
        ))}
      </section>
    </>
  );
}

function McpEndpointFields({
  configuration,
  transport,
  onTransport
}: {
  configuration: unknown;
  transport: McpTransport;
  onTransport: (transport: McpTransport) => void;
}) {
  const record = configurationRecord(configuration);
  const args = Array.isArray(record.args)
    ? record.args.filter((item) => typeof item === "string")
    : [];
  return (
    <>
      <label>
        Trust domain
        <input
          name="trust_domain"
          required
          defaultValue={stringValue(record.trust_domain, "local")}
        />
      </label>
      <fieldset className="transport-control">
        <legend>Transport</legend>
        {(["stdio", "streamable_http", "legacy_sse"] as const).map((value) => (
          <label key={value}>
            <input
              type="radio"
              name="transport"
              value={value}
              checked={transport === value}
              onChange={() => onTransport(value)}
            />
            {value === "stdio" ? "stdio" : value === "streamable_http" ? "HTTP" : "SSE"}
          </label>
        ))}
      </fieldset>
      {transport === "stdio" ? (
        <>
          <label>
            Executable
            <input name="program" required defaultValue={stringValue(record.program)} />
          </label>
          <label>
            Arguments
            <textarea name="args" rows={4} className="mono" defaultValue={args.join("\n")} />
          </label>
          <label>
            Environment
            <textarea
              name="env"
              rows={5}
              className="mono"
              defaultValue={JSON.stringify(configurationRecord(record.env), null, 2)}
            />
          </label>
        </>
      ) : (
        <label>
          Server URL
          <input name="url" type="url" required defaultValue={stringValue(record.url)} />
        </label>
      )}
    </>
  );
}

function A2aEndpointFields({ configuration }: { configuration: unknown }) {
  const record = configurationRecord(configuration);
  return (
    <>
      <label>
        Agent Card URL
        <input name="url" type="url" required defaultValue={stringValue(record.url)} />
      </label>
      <div className="protocol-facts" aria-label="A2A protocol capabilities">
        <span>Agent Card</span>
        <span>Task delegation</span>
        <span>Status sync</span>
      </div>
    </>
  );
}

function endpointActionLabel(kind: "llm" | "mcp" | "a2a", action: "test" | "refresh") {
  if (action === "test") {
    return kind === "llm" ? "Test provider" : kind === "mcp" ? "Connect server" : "Test protocol";
  }
  return kind === "llm"
    ? "Refresh models"
    : kind === "mcp"
      ? "Discover capabilities"
      : "Refresh Agent Card";
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function defaultProfile(): AgentProfile {
  return {
    id: crypto.randomUUID(),
    version: 1,
    name: "",
    instructions: "",
    trust_domain: "local",
    model_grants: [],
    tool_grants: [],
    delegation_grants: [],
    input_schema: null,
    output_schema: null,
    context_policy: {
      include_parent_messages: true,
      include_artifacts: true,
      max_message_count: 100,
      allowed_labels: []
    },
    limits: {
      max_depth: 8,
      max_fan_out: 16,
      max_steps: 100,
      max_concurrency: 8,
      max_repeated_fingerprint: 3,
      deadline_ms: 3600000,
      budget_microunits: 10000000
    }
  };
}

function AgentForm({ defaults, onDone }: { defaults?: CreationDefaults; onDone: () => void }) {
  const client = useQueryClient();
  const [formError, setFormError] = useState<Error | null>(null);
  const initial = {
    ...defaultProfile(),
    model_grants: defaults?.modelGrants ?? [],
    tool_grants: defaults?.toolGrants ?? [],
    delegation_grants: defaults?.delegationGrants ?? []
  };
  const create = useMutation({
    mutationFn: (profile: AgentProfile) =>
      apiRequest("/v1/admin/fabric/profiles", agentProfileRecordSchema, {
        method: "POST",
        body: JSON.stringify({ profile })
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["profiles"] });
      onDone();
    }
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    const form = new FormData(event.currentTarget);
    try {
      const parsed = agentProfileSchema.parse({
        id: text(form.get("id")),
        version: Number(form.get("version")),
        name: text(form.get("name")),
        instructions: text(form.get("instructions")),
        trust_domain: text(form.get("trust_domain")),
        model_grants: list(form.get("model_grants")),
        tool_grants: list(form.get("tool_grants")),
        delegation_grants: list(form.get("delegation_grants")),
        input_schema: parseOptionalJson(form.get("input_schema"), "Input schema"),
        output_schema: parseOptionalJson(form.get("output_schema"), "Output schema"),
        context_policy: {
          include_parent_messages: form.get("include_parent_messages") === "on",
          include_artifacts: form.get("include_artifacts") === "on",
          max_message_count: Number(form.get("max_message_count")),
          allowed_labels: list(form.get("allowed_labels"))
        },
        limits: {
          max_depth: Number(form.get("max_depth")),
          max_fan_out: Number(form.get("max_fan_out")),
          max_steps: Number(form.get("max_steps")),
          max_concurrency: Number(form.get("max_concurrency")),
          max_repeated_fingerprint: Number(form.get("max_repeated_fingerprint")),
          deadline_ms: Number(form.get("deadline_ms")),
          budget_microunits: Number(form.get("budget_microunits"))
        }
      });
      create.mutate(parsed);
    } catch (error) {
      setFormError(
        error instanceof z.ZodError ? formatProfileValidationError(error) : toError(error)
      );
    }
  }
  return (
    <InspectorForm onSubmit={submit} error={formError ?? create.error}>
      <label>
        ID
        <input name="id" required readOnly defaultValue={initial.id} />
      </label>
      <label>
        Initial version
        <input name="version" type="number" min="1" required readOnly defaultValue={1} />
      </label>
      <label>
        Name
        <input name="name" required defaultValue={initial.name} />
      </label>
      <label>
        Trust domain
        <input name="trust_domain" required defaultValue={initial.trust_domain} />
      </label>
      <label>
        Instructions
        <textarea name="instructions" rows={5} required defaultValue={initial.instructions} />
      </label>
      <label>
        Model grants
        <input name="model_grants" defaultValue={initial.model_grants.join(", ")} />
      </label>
      <label>
        Tool grants
        <input name="tool_grants" defaultValue={initial.tool_grants.join(", ")} />
      </label>
      <label>
        Delegation grants
        <input name="delegation_grants" defaultValue={initial.delegation_grants.join(", ")} />
      </label>
      <AgentFormSection
        title="Context policy"
        description={`${String(initial.context_policy.max_message_count)} message limit`}
        icon={<SlidersHorizontal size={14} />}
      >
        <label className="check-row">
          <input
            type="checkbox"
            name="include_parent_messages"
            defaultChecked={initial.context_policy.include_parent_messages}
          />
          Include parent messages
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            name="include_artifacts"
            defaultChecked={initial.context_policy.include_artifacts}
          />
          Include artifacts
        </label>
        <label>
          Maximum message count
          <input
            name="max_message_count"
            type="number"
            min="0"
            required
            defaultValue={initial.context_policy.max_message_count}
          />
        </label>
        <label>
          Allowed labels
          <input
            name="allowed_labels"
            defaultValue={initial.context_policy.allowed_labels.join(", ")}
            placeholder="Optional, comma separated"
          />
        </label>
      </AgentFormSection>
      <AgentFormSection
        title="Execution limits"
        description={`${String(initial.limits.max_steps)} steps / ${String(initial.limits.max_concurrency)} parallel`}
        icon={<SlidersHorizontal size={14} />}
      >
        <div className="field-pair">
          <NumberField name="max_depth" label="Maximum depth" value={initial.limits.max_depth} />
          <NumberField
            name="max_fan_out"
            label="Maximum fan out"
            value={initial.limits.max_fan_out}
          />
        </div>
        <div className="field-pair">
          <NumberField name="max_steps" label="Maximum steps" value={initial.limits.max_steps} />
          <NumberField
            name="max_concurrency"
            label="Max concurrency"
            value={initial.limits.max_concurrency}
          />
        </div>
        <NumberField
          name="max_repeated_fingerprint"
          label="Repeated fingerprint limit"
          value={initial.limits.max_repeated_fingerprint}
        />
        <NumberField name="deadline_ms" label="Deadline (ms)" value={initial.limits.deadline_ms} />
        <NumberField
          name="budget_microunits"
          label="Budget (microunits)"
          value={initial.limits.budget_microunits}
        />
      </AgentFormSection>
      <AgentFormSection
        title="Protocol contract"
        description={
          initial.input_schema || initial.output_schema ? "JSON schemas configured" : "Optional"
        }
        icon={<Braces size={14} />}
      >
        <label>
          Input schema
          <textarea
            className="mono"
            name="input_schema"
            rows={7}
            defaultValue={formatOptionalJson(initial.input_schema)}
            placeholder="Empty accepts any input"
          />
        </label>
        <label>
          Output schema
          <textarea
            className="mono"
            name="output_schema"
            rows={7}
            defaultValue={formatOptionalJson(initial.output_schema)}
            placeholder="Empty accepts any output"
          />
        </label>
      </AgentFormSection>
      <button className="primary-button" type="submit" disabled={create.isPending}>
        <Save size={15} /> Create profile
      </button>
    </InspectorForm>
  );
}

function AgentDetail({ value, onDone }: { value: AgentProfileRecord; onDone: () => void }) {
  return (
    <DetailList
      rows={[
        ["ID", value.profile.id],
        ["Resource", value.resource_id],
        ["Version", String(value.version)],
        ["Trust domain", value.profile.trust_domain],
        ["Model grants", value.profile.model_grants.join(", ")],
        ["Tool grants", value.profile.tool_grants.join(", ")],
        ["Delegation grants", value.profile.delegation_grants.join(", ")]
      ]}
    >
      <p className="inspector-inline-state">{value.profile.instructions}</p>
      <button className="quiet-button" type="button" onClick={onDone}>
        Close
      </button>
    </DetailList>
  );
}

function AgentFormSection({
  title,
  description,
  icon,
  children
}: {
  title: string;
  description: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <details className="agent-form-section">
      <summary>
        <span className="agent-form-section-icon">{icon}</span>
        <span>
          <strong>{title}</strong>
          <small>{description}</small>
        </span>
        <ChevronDown className="agent-form-chevron" size={14} />
      </summary>
      <div className="agent-form-section-fields">{children}</div>
    </details>
  );
}

function NumberField({ name, label, value }: { name: string; label: string; value: number }) {
  return (
    <label>
      {label}
      <input name={name} type="number" min="0" required defaultValue={value} />
    </label>
  );
}

function formatOptionalJson(value: unknown): string {
  return value === null ? "" : JSON.stringify(value, null, 2);
}

function VirtualModelForm({
  model,
  defaults = [],
  onDone
}: {
  model?: VirtualModel;
  defaults?: ModelRoute[];
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [routes, setRoutes] = useState<ModelRoute[]>(() => model?.routes ?? defaults);
  const [formError, setFormError] = useState<Error | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const resources = useQuery({
    queryKey: ["resources"],
    queryFn: () => apiRequest("/v1/admin/fabric/resources", fabricResourceListSchema)
  });
  const profiles = useQuery({
    queryKey: ["profiles"],
    queryFn: () => apiRequest("/v1/admin/fabric/profiles", profileListSchema)
  });
  const channels = (resources.data?.data ?? []).filter((resource) => resource.kind === "channel");
  const create = useMutation({
    mutationFn: (body: { public_name: string; routes: ModelRoute[] }) =>
      apiRequest("/v1/admin/fabric/models", virtualModelSchema, {
        method: "POST",
        body: JSON.stringify(body)
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["virtual-models"] });
      onDone();
    }
  });
  const publish = useMutation({
    mutationFn: (body: { public_name: string; routes: ModelRoute[] }) =>
      apiRequest(
        `/v1/admin/fabric/models/${encodeURIComponent(model?.id ?? "")}/publish`,
        virtualModelSchema,
        {
          method: "POST",
          body: JSON.stringify({
            routes: body.routes,
            expected_revision: model?.active_revision ?? 1
          })
        }
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["virtual-models"] });
      onDone();
    }
  });
  const remove = useMutation({
    mutationFn: () =>
      apiRequest(`/v1/admin/fabric/models/${encodeURIComponent(model?.id ?? "")}`, z.null(), {
        method: "DELETE"
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["virtual-models"] });
      onDone();
    }
  });
  function reindex(updated: ModelRoute[]) {
    return updated.map((route, ordinal) => ({ ...route, ordinal }));
  }
  function updateRoute(index: number, update: ModelRoute) {
    setRoutes((current) =>
      current.map((route, currentIndex) => (currentIndex === index ? update : route))
    );
  }
  function moveRoute(index: number, offset: -1 | 1) {
    setRoutes((current) => {
      const target = index + offset;
      if (target < 0 || target >= current.length) return current;
      const route = current[index];
      const targetRoute = current[target];
      if (!route || !targetRoute) return current;
      const next = [...current];
      next[index] = targetRoute;
      next[target] = route;
      return reindex(next);
    });
  }
  function addRoute() {
    const capability = channels.flatMap(channelModelCapabilities)[0];
    if (!capability) {
      setFormError(new Error("Refresh an LLM channel to discover a model before adding a route"));
      return;
    }
    setFormError(null);
    setRoutes((current) => [
      ...current,
      {
        ordinal: current.length,
        target_kind: "capability",
        target_id: capability.id,
        conversion_class: "lossless",
        route_config: {}
      }
    ]);
  }
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    const form = new FormData(event.currentTarget);
    if (routes.length === 0) {
      setFormError(new Error("At least one fallback route is required"));
      return;
    }
    const routeKeys = new Set(routes.map((route) => `${route.target_kind}:${route.target_id}`));
    if (routeKeys.size !== routes.length) {
      setFormError(new Error("Fallback routes must be unique"));
      return;
    }
    const body = {
      public_name: text(form.get("name")).trim(),
      routes: reindex(routes)
    };
    if (model) publish.mutate(body);
    else create.mutate(body);
  }
  const saveError = model ? publish.error : create.error;
  return (
    <InspectorForm
      onSubmit={submit}
      error={formError ?? resources.error ?? profiles.error ?? saveError ?? remove.error}
    >
      <label>
        Public model alias
        <input
          name="name"
          required
          defaultValue={model?.public_name ?? ""}
          placeholder="smart-code"
        />
      </label>
      <section className="endpoint-collection" aria-label="Fallback routes">
        <div className="endpoint-section-heading">
          <span className="field-label">Fallback order</span>
          <button className="quiet-button compact" type="button" onClick={addRoute}>
            + Route
          </button>
        </div>
        {routes.length === 0 && (
          <p className="inspector-inline-state">Add at least one provider model</p>
        )}
        {routes.map((route, index) => (
          <fieldset
            className="virtual-model-route"
            key={`${String(index)}:${route.target_kind}:${route.target_id}`}
          >
            <legend>Priority {String(index + 1)}</legend>
            <label>
              Route target
              <select
                value={route.target_kind}
                onChange={(event) =>
                  updateRoute(index, {
                    ...route,
                    target_kind: event.target.value as ModelRoute["target_kind"],
                    target_id: ""
                  })
                }
              >
                <option value="capability">Channel model capability</option>
                <option value="agent_profile">Agent profile</option>
              </select>
            </label>
            {route.target_kind === "capability" ? (
              <CapabilityTargetSelect
                channels={channels}
                targetId={route.target_id}
                onChange={(targetId) => updateRoute(index, { ...route, target_id: targetId })}
              />
            ) : (
              <label>
                Agent profile
                <select
                  required
                  value={route.target_id}
                  onChange={(event) =>
                    updateRoute(index, { ...route, target_id: event.target.value })
                  }
                >
                  <option value="" disabled>
                    Select profile
                  </option>
                  {profiles.data?.data.map((record) => (
                    <option value={record.resource_id} key={record.resource_id}>
                      {record.profile.name} · v{record.version}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Conversion class
              <select
                value={route.conversion_class}
                onChange={(event) =>
                  updateRoute(index, {
                    ...route,
                    conversion_class: event.target.value as ModelRoute["conversion_class"]
                  })
                }
              >
                <option value="lossless">Lossless</option>
                <option value="compatible">Compatible</option>
                <option value="unsupported">Unsupported</option>
              </select>
            </label>
            <div className="route-order-actions">
              <button
                className="icon-button"
                type="button"
                title="Move route up"
                disabled={index === 0}
                onClick={() => moveRoute(index, -1)}
              >
                <ChevronUp size={14} />
              </button>
              <button
                className="icon-button"
                type="button"
                title="Move route down"
                disabled={index === routes.length - 1}
                onClick={() => moveRoute(index, 1)}
              >
                <ChevronDown size={14} />
              </button>
              <button
                className="icon-button danger"
                type="button"
                title="Remove route"
                onClick={() =>
                  setRoutes((current) => reindex(current.filter((_, item) => item !== index)))
                }
              >
                <Trash2 size={14} />
              </button>
            </div>
          </fieldset>
        ))}
      </section>
      <div className="inspector-actions">
        <button
          className="primary-button"
          type="submit"
          disabled={create.isPending || publish.isPending}
        >
          <Save size={15} /> {model ? "Publish revision" : "Create virtual model"}
        </button>
        {model && (
          <button
            className="icon-button danger"
            type="button"
            title="Delete virtual model"
            disabled={remove.isPending}
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title="Delete virtual model?"
          description={`${model?.public_name ?? "This model"} will be removed from routing. Agent Profile grants are not rewritten.`}
          confirmLabel="Delete virtual model"
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            remove.mutate();
          }}
        />
      )}
    </InspectorForm>
  );
}

function CapabilityTargetSelect({
  channels,
  targetId,
  onChange
}: {
  channels: FabricResource[];
  targetId: string;
  onChange: (targetId: string) => void;
}) {
  const [channelId, setChannelId] = useState("");
  useEffect(() => {
    if (channelId || channels.length === 0) return;
    const owned =
      channels.find((channel) =>
        channel.capabilities.some((capability) => capability.id === targetId)
      ) ?? channels[0];
    if (owned) setChannelId(owned.id);
  }, [channelId, channels, targetId]);
  const channel = channels.find((candidate) => candidate.id === channelId);
  const capabilities = channel ? channelModelCapabilities(channel) : [];
  return (
    <>
      <label>
        LLM channel
        <select
          value={channelId}
          onChange={(event) => {
            const nextChannelId = event.target.value;
            setChannelId(nextChannelId);
            const next = channels.find((candidate) => candidate.id === nextChannelId);
            const first = next ? channelModelCapabilities(next)[0] : undefined;
            onChange(first?.id ?? "");
          }}
        >
          {channels.map((candidate) => (
            <option value={candidate.id} key={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Upstream model capability
        <select required value={targetId} onChange={(event) => onChange(event.target.value)}>
          <option value="" disabled>
            Select model
          </option>
          {capabilities.map((capability) => (
            <option value={capability.id} key={capability.id}>
              {capabilityUpstreamModel(capability)}
              {capability.enabled ? "" : " (pending)"}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

function KeyForm({ defaults, onDone }: { defaults?: CreationDefaults; onDone: () => void }) {
  const client = useQueryClient();
  const [secret, setSecret] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const applications = useQuery({
    queryKey: ["applications"],
    queryFn: () => apiRequest("/v1/admin/applications", applicationListSchema)
  });
  const create = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest(`/v1/admin/applications/${applicationId}/keys`, createdKeySchema, {
        method: "POST",
        body: JSON.stringify(body)
      }),
    onSuccess: async (value) => {
      setSecret(value.key);
      await client.invalidateQueries({ queryKey: ["keys", applicationId] });
    }
  });
  if (secret) return <OneTimeSecret secret={secret} onDone={onDone} />;
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      scopes: {
        endpoints: list(form.get("endpoints")),
        models: list(form.get("models")),
        tools: list(form.get("tools")),
        agents:
          defaults && defaults.agentScopes.length > 0
            ? defaults.agentScopes
            : list(form.get("agents"))
      }
    });
  }
  return (
    <InspectorForm onSubmit={submit} error={applications.error ?? create.error}>
      <label>
        Application
        <select
          required
          value={applicationId}
          onChange={(event) => setApplicationId(event.target.value)}
        >
          <option value="" disabled>
            Select an application
          </option>
          {applications.data?.data.map((application) => (
            <option value={application.id} key={application.id}>
              {application.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Endpoint scopes
        <input name="endpoints" defaultValue="responses.create, models.list" required />
      </label>
      <label>
        Model scopes
        <input name="models" defaultValue="*" required />
      </label>
      <label>
        Tool scopes
        <input name="tools" defaultValue="*" required />
      </label>
      <label>
        Agent scopes
        <input
          name="agents"
          defaultValue={
            defaults && defaults.agentScopes.length > 0 ? defaults.agentScopes.join(", ") : "*"
          }
          required
        />
      </label>
      <button
        className="primary-button"
        type="submit"
        disabled={create.isPending || !applicationId}
      >
        <Save size={15} /> Create key
      </button>
    </InspectorForm>
  );
}

function OneTimeSecret({ secret, onDone }: { secret: string; onDone: () => void }) {
  return (
    <div className="one-time-secret">
      <strong>Copy this key now</strong>
      <code>{secret}</code>
      <span>It will not be shown again.</span>
      <div>
        <button
          className="quiet-button"
          type="button"
          onClick={() => void navigator.clipboard.writeText(secret)}
        >
          <Copy size={15} /> Copy
        </button>
        <button className="primary-button" type="button" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function KeyDetail({ value, onDone }: { value: VirtualKey; onDone: () => void }) {
  const client = useQueryClient();
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const revoke = useMutation({
    mutationFn: () =>
      apiRequest(
        `/v1/admin/applications/${encodeURIComponent(value.application_id)}/keys/${value.id}`,
        z.null(),
        { method: "DELETE" }
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["keys", value.application_id] });
      onDone();
    }
  });
  return (
    <div className="key-runtime-inspector">
      <DetailList
        rows={[
          ["ID", value.id],
          ["Application", value.application_id],
          ["Prefix", value.prefix],
          ["Status", value.revoked_at ? "Revoked" : "Active"],
          ["Created", new Date(value.created_at).toLocaleString()],
          ["Expires", value.expires_at ? new Date(value.expires_at).toLocaleString() : "Never"],
          ["Endpoints", value.scopes.endpoints.join(", ")],
          ["Models", value.scopes.models.join(", ")],
          ["Tools", value.scopes.tools.join(", ")],
          ["Agents", value.scopes.agents.join(", ")]
        ]}
      />
      <div className="inspector-actions">
        {!value.revoked_at && (
          <button
            className="quiet-button danger"
            type="button"
            onClick={() => setConfirmRevoke(true)}
          >
            <Ban size={15} /> Revoke key
          </button>
        )}
        <button className="quiet-button" type="button" onClick={onDone}>
          Close
        </button>
      </div>
      {confirmRevoke && (
        <ConfirmDialog
          title="Revoke virtual key?"
          description={`Requests authenticated with ${value.prefix}… will stop after this change.`}
          confirmLabel="Revoke key"
          pending={revoke.isPending}
          onCancel={() => setConfirmRevoke(false)}
          onConfirm={() => {
            setConfirmRevoke(false);
            revoke.mutate();
          }}
        />
      )}
    </div>
  );
}

function emptyTaskGraph() {
  return {
    id: crypto.randomUUID(),
    nodes: [
      {
        id: crypto.randomUUID(),
        name: "prepare",
        kind: "transform",
        depth: 0,
        capability: null,
        side_effect: "none",
        configuration: {},
        status: "pending",
        attempt_count: 0,
        idempotency_key: null,
        side_effect_confirmed: false,
        output: null,
        error_code: null,
        error_message: null,
        updated_at: ""
      }
    ],
    edges: [],
    created_at: ""
  };
}

function TaskForm({ defaults, onDone }: { defaults?: CreationDefaults; onDone: () => void }) {
  const client = useQueryClient();
  const [message, setMessage] = useState("");
  const [graphEditorError, setGraphEditorError] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState("");
  const [graph, setGraph] = useState<TaskGraph>(emptyTaskGraph);
  const applications = useQuery({
    queryKey: ["applications"],
    queryFn: () => apiRequest("/v1/admin/applications", applicationListSchema)
  });
  const create = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest("/v1/admin/tasks", taskCreatedSchema, {
        method: "POST",
        body: JSON.stringify(body)
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["tasks"] });
      onDone();
    }
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    if (!applicationId) {
      setMessage("Select an Application before creating a task.");
      return;
    }
    const graphError = graphEditorError ?? validateTaskGraph(graph);
    if (graphError) {
      setMessage(graphError);
      return;
    }
    const form = new FormData(event.currentTarget);
    create.mutate({
      application_id: applicationId,
      public_model: text(form.get("public_model")).trim() || "synapse-durable-task",
      protocol: "openai",
      background: form.get("background") === "on",
      graph: normalizeTaskGraph(graph)
    });
  }
  return (
    <InspectorForm onSubmit={submit} error={applications.error ?? create.error} message={message}>
      <div className="automation-steps" aria-label="Durable task workflow">
        {["Application", "Graph", "Start"].map((step, index) => (
          <span key={step} className={index === 0 ? "is-active" : ""}>
            <b>{index + 1}</b> {step}
          </span>
        ))}
      </div>
      <label>
        Application
        <select
          required
          value={applicationId}
          onChange={(event) => setApplicationId(event.target.value)}
        >
          <option value="" disabled>
            Select an application
          </option>
          {applications.data?.data.map((application) => (
            <option value={application.id} key={application.id}>
              {application.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Public model
        <input
          name="public_model"
          defaultValue="synapse-durable-task"
          placeholder="synapse-durable-task"
        />
      </label>
      {defaults?.profileId && (
        <p className="inspector-inline-state">
          Derived from {defaults.profileId} v{defaults.profileVersion}
        </p>
      )}
      <TaskGraphEditor value={graph} onChange={setGraph} onValidationChange={setGraphEditorError} />
      <label className="check-row">
        <input name="background" type="checkbox" defaultChecked />
        Run in background
      </label>
      <div className="automation-actions">
        <button
          className="primary-button"
          type="submit"
          disabled={create.isPending || !applicationId}
        >
          <Play size={15} /> {create.isPending ? "Creating…" : "Create and start"}
        </button>
      </div>
    </InspectorForm>
  );
}

function TaskRuntimeInspector({ value }: { value: TaskSummary }) {
  const client = useQueryClient();
  const [confirmation, setConfirmation] = useState<ConfirmIntent | null>(null);
  const detail = useQuery({
    queryKey: ["task", value.id],
    queryFn: () => apiRequest(`/v1/admin/tasks/${value.id}`, taskDetailEnvelopeSchema),
    refetchInterval: 2_500
  });
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["tasks"] }),
      client.invalidateQueries({ queryKey: ["task", value.id] })
    ]);
  };
  const cancel = useMutation({
    mutationFn: () =>
      apiRequest(`/v1/admin/tasks/${value.id}/cancel`, taskCanceledSchema, { method: "POST" }),
    onSuccess: refresh
  });
  const approval = useMutation({
    mutationFn: ({ approvalId, approved }: { approvalId: string; approved: boolean }) =>
      apiRequest(`/v1/admin/approvals/${approvalId}/decision`, approvalDecisionSchema, {
        method: "POST",
        body: JSON.stringify({ approved })
      }),
    onSuccess: refresh
  });
  const download = useMutation({
    mutationFn: async ({ artifactId, mediaType }: { artifactId: string; mediaType: string }) => {
      const blob = await apiDownload(`/v1/admin/tasks/${value.id}/artifacts/${artifactId}`);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${artifactId}.${artifactExtension(mediaType)}`;
      anchor.click();
      URL.revokeObjectURL(url);
    }
  });
  const detailData = detail.data?.task;
  const attempts =
    detailData?.graph?.nodes.reduce((total, run) => total + run.attempt_count, 0) ?? 0;
  return (
    <div className="task-runtime-inspector">
      <div className="task-runtime-summary">
        <RuntimeMetric label="State" value={value.status.replaceAll("_", " ")} />
        <RuntimeMetric label="Nodes" value={String(detailData?.graph?.nodes.length ?? 0)} />
        <RuntimeMetric label="Attempts" value={String(attempts)} />
        <RuntimeMetric label="Artifacts" value={String(detailData?.artifacts.length ?? 0)} />
      </div>
      <div className="inspector-actions">
        {!["completed", "failed", "canceled"].includes(value.status) && (
          <button
            className="quiet-button danger"
            type="button"
            onClick={() =>
              setConfirmation({
                title: "Cancel task?",
                description:
                  "Running work will stop after the current durable transition. Completed side effects are not rolled back.",
                confirmLabel: "Cancel task",
                action: () => cancel.mutate()
              })
            }
          >
            <Ban size={15} /> Cancel
          </button>
        )}
        <Link className="quiet-button" to={`/tasks/${value.id}`}>
          <ExternalLink size={15} /> Open execution
        </Link>
      </div>
      {detail.isLoading && <p className="inspector-inline-state">Loading execution detail</p>}
      {detail.error instanceof Error && (
        <p className="form-message error">{detail.error.message}</p>
      )}
      {detailData && (
        <TaskRuntimeDetail
          detail={detailData}
          approve={(approvalId, approved) => {
            if (approved) {
              approval.mutate({ approvalId, approved });
              return;
            }
            setConfirmation({
              title: "Deny approval gate?",
              description:
                "The waiting node will be denied and the task may no longer be able to complete.",
              confirmLabel: "Deny gate",
              action: () => approval.mutate({ approvalId, approved: false })
            });
          }}
          downloadArtifact={(artifactId, mediaType) => download.mutate({ artifactId, mediaType })}
        />
      )}
      {confirmation && (
        <ConfirmDialog
          title={confirmation.title}
          description={confirmation.description}
          confirmLabel={confirmation.confirmLabel}
          pending={cancel.isPending || approval.isPending}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            confirmation.action();
            setConfirmation(null);
          }}
        />
      )}
    </div>
  );
}

function TaskRuntimeDetail({
  detail,
  approve,
  downloadArtifact
}: {
  detail: TaskDetailData;
  approve: (approvalId: string, approved: boolean) => void;
  downloadArtifact: (artifactId: string, mediaType: string) => void;
}) {
  const nodes = detail.graph?.nodes ?? [];
  return (
    <>
      {nodes.length > 0 && (
        <TaskSection title="Execution nodes" count={nodes.length}>
          {nodes.map((run) => (
            <div className="runtime-node-row" key={run.id}>
              <i className={`runtime-state state-${run.status}`} />
              <div>
                <strong>{run.name}</strong>
                <small>
                  {run.kind} / {String(run.attempt_count)} attempts
                </small>
                {run.error_message && <span className="runtime-error">{run.error_message}</span>}
              </div>
            </div>
          ))}
        </TaskSection>
      )}
      {detail.approvals.length > 0 && (
        <TaskSection title="Approval gates" count={detail.approvals.length}>
          {detail.approvals.map((item) => (
            <div className="runtime-approval-row" key={item.id}>
              <AlertTriangle size={14} />
              <div>
                <strong>{item.node_name || item.node_id}</strong>
                <small>{item.reason ?? item.status}</small>
              </div>
              {item.status === "pending" && (
                <div className="runtime-row-actions">
                  <button
                    className="icon-button approve"
                    type="button"
                    title="Approve task gate"
                    onClick={() => approve(item.id, true)}
                  >
                    <Check size={14} />
                  </button>
                  <button
                    className="icon-button danger"
                    type="button"
                    title="Deny task gate"
                    onClick={() => approve(item.id, false)}
                  >
                    <Ban size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </TaskSection>
      )}
      {detail.artifacts.length > 0 && (
        <TaskSection title="Artifacts" count={detail.artifacts.length}>
          {detail.artifacts.map((artifact) => (
            <div className="runtime-artifact-row" key={artifact.id}>
              <FileStack size={14} />
              <div>
                <strong>
                  {Object.keys(artifact.labels).join(", ") || artifact.id.slice(0, 13)}
                </strong>
                <small>
                  {artifact.media_type} / {formatBytes(artifact.size_bytes)}
                </small>
              </div>
              <button
                className="icon-button"
                type="button"
                title="Download artifact"
                onClick={() => downloadArtifact(artifact.id, artifact.media_type)}
              >
                <Download size={14} />
              </button>
            </div>
          ))}
        </TaskSection>
      )}
    </>
  );
}

function RuntimeMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function TaskSection({
  title,
  count,
  children
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="task-inspector-section">
      <header>
        <strong>{title}</strong>
        <span>{count}</span>
      </header>
      <div>{children}</div>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function artifactExtension(mediaType: string): string {
  const subtype = mediaType.split("/")[1]?.split(";")[0];
  return subtype?.replace("plain", "txt") ?? "bin";
}

export function budgetPercent(spent: number, hardLimit: number): number {
  if (hardLimit === 0) return spent > 0 ? 100 : 0;
  return Math.min(100, Math.round((spent / hardLimit) * 100));
}

function formatMoney(microunits: number): string {
  return `$${(microunits / 1_000_000).toFixed(4)}`;
}

function CapabilityDetail({ value, onDone }: { value: Capability; onDone: () => void }) {
  const client = useQueryClient();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const update = useMutation({
    mutationFn: (enabled: boolean) =>
      apiRequest(`/v1/admin/fabric/capabilities/${value.id}/approve`, z.null(), {
        method: "POST",
        body: JSON.stringify({ enabled })
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["resources"] });
      onDone();
    }
  });
  return (
    <DetailList
      rows={[
        ["Upstream model", capabilityUpstreamModel(value)],
        ["Namespace", value.namespace],
        ["Kind", value.kind],
        ["Channel", value.resource_name],
        ["Status", value.enabled ? "Approved" : "Pending"],
        ["Estimated cost", formatMoney(capabilityEstimatedCost(value))],
        ["Latency", `${String(capabilityLatencyMs(value))}ms`]
      ]}
    >
      <div className="inspector-actions">
        {value.enabled ? (
          <button
            className="quiet-button danger"
            type="button"
            onClick={() => setConfirmDisable(true)}
          >
            <Ban size={15} /> Disable
          </button>
        ) : (
          <button
            className="primary-button"
            type="button"
            disabled={update.isPending}
            onClick={() => update.mutate(true)}
          >
            <Check size={15} /> Approve
          </button>
        )}
      </div>
      {confirmDisable && (
        <ConfirmDialog
          title="Disable capability?"
          description={`${value.namespace} will be ignored by routing until it is approved again.`}
          confirmLabel="Disable capability"
          pending={update.isPending}
          onCancel={() => setConfirmDisable(false)}
          onConfirm={() => {
            setConfirmDisable(false);
            update.mutate(false);
          }}
        />
      )}
    </DetailList>
  );
}

interface ConfirmIntent {
  title: string;
  description: string;
  confirmLabel: string;
  action: () => void;
}

function ConfirmDialog({
  title,
  description,
  confirmLabel,
  pending,
  onCancel,
  onConfirm
}: {
  title: string;
  description: string;
  confirmLabel: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
    return () => dialog.current?.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="confirm-dialog"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-description"
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <div className="confirm-dialog-icon" aria-hidden="true">
        <AlertTriangle size={17} />
      </div>
      <div className="confirm-dialog-copy">
        <strong id="confirm-title">{title}</strong>
        <p id="confirm-description">{description}</p>
      </div>
      <div className="confirm-dialog-actions">
        <button className="quiet-button bordered" type="button" onClick={onCancel}>
          Keep current
        </button>
        <button className="destructive-button" type="button" disabled={pending} onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}

function InspectorForm({
  children,
  onSubmit,
  error,
  message,
  formRef
}: {
  children: ReactNode;
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void;
  error?: Error | null;
  message?: string;
  formRef?: Ref<HTMLFormElement>;
}) {
  return (
    <form ref={formRef} className="inspector-form" onSubmit={onSubmit}>
      {children}
      {error && <p className="form-message error">{error.message}</p>}
      {message && <p className="form-message">{message}</p>}
    </form>
  );
}

function DetailList({ rows, children }: { rows: [string, string][]; children?: ReactNode }) {
  return (
    <div className="detail-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value || "-"}</strong>
        </div>
      ))}
      {children}
    </div>
  );
}

function text(value: FormDataEntryValue | null): string {
  return typeof value === "string" ? value : "";
}
function list(value: FormDataEntryValue | null): string[] {
  return text(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error("Resource is invalid");
}
